// src/Components/modules/Authorizations/VersionesAutorizacionDialog.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  List,
  Avatar,
  Typography,
  Card,
  Divider,
  Space,
  Button,
  Spin,
  Tag,
  Empty,
} from "antd";
import {
  HistoryOutlined,
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Text } = Typography;

function VersionesAutorizacionDialog({
  open,
  onClose,
  autorizacionId,
  onViewVersion,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ versiones: [], version_actual: 1 });

  useEffect(() => {
    if (autorizacionId && open) {
      setLoading(true);
      api
        .get(`/autorizaciones/${autorizacionId}/versiones`)
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error al obtener las versiones:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [autorizacionId, open]);

  const getStatusColor = (estado) => {
    if (!estado) return "default";
    switch (estado.toLowerCase()) {
      case "activa":
        return "geekblue";
      case "suspendida":
        return "volcano";
      case "completada":
        return "green";
      case "cerrada":
        return "purple";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <HistoryOutlined style={{ color: "#722ed1" }} />
          <Text strong style={{ color: "#2E3B4E" }}>
            Versiones de la Autorizacion
          </Text>
          <Tag color="purple">Version actual: {data.version_actual}</Tag>
        </Space>
      }
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={800}
      centered
      style={{ borderRadius: "15px" }}
    >
      <div style={{ backgroundColor: "#f9f9f9", padding: "20px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "10px" }}>Cargando versiones...</div>
          </div>
        ) : data.versiones.length === 0 ? (
          <Empty
            description="No hay versiones anteriores disponibles"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            itemLayout="vertical"
            dataSource={data.versiones}
            renderItem={(version, index) => (
              <div key={version.id}>
                <Card
                  style={{ borderRadius: "8px", backgroundColor: "#fff" }}
                  variant="borderless"
                >
                  <List.Item
                    actions={[
                      <Button
                        key="ver"
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
                        onClick={() => onViewVersion(version.numero_version)}
                      >
                        Ver Version
                      </Button>,
                    ]}
                  >
                    <Space align="start" size="large" style={{ width: "100%" }}>
                      <Avatar
                        style={{ backgroundColor: "#722ed1" }}
                        icon={<HistoryOutlined />}
                      >
                        {version.numero_version}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <Space direction="vertical" size="small">
                          <Text
                            strong
                            style={{ color: "#2E3B4E", fontSize: "16px" }}
                          >
                            Version {version.numero_version}
                          </Text>
                          <Space wrap>
                            {version.numero_autorizacion && (
                              <Tag color="blue">
                                AUT: {version.numero_autorizacion}
                              </Tag>
                            )}
                            {version.numero_pre_autorizacion && (
                              <Tag color="cyan">
                                PRE: {version.numero_pre_autorizacion}
                              </Tag>
                            )}
                            {version.estado_autorizacion && (
                              <Tag color={getStatusColor(version.estado_autorizacion)}>
                                {version.estado_autorizacion}
                              </Tag>
                            )}
                          </Space>
                          <Space>
                            <UserOutlined style={{ color: "#8c8c8c" }} />
                            <Text type="secondary">
                              {version.usuario?.nombre || "Usuario desconocido"}
                            </Text>
                            <Divider type="vertical" />
                            <ClockCircleOutlined style={{ color: "#8c8c8c" }} />
                            <Text type="secondary">
                              {formatDate(version.fecha_version)}
                            </Text>
                          </Space>
                        </Space>
                      </div>
                    </Space>
                  </List.Item>
                </Card>
                {index < data.versiones.length - 1 && <Divider />}
              </div>
            )}
          />
        )}
      </div>
    </Modal>
  );
}

VersionesAutorizacionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  autorizacionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onViewVersion: PropTypes.func.isRequired,
};

export default VersionesAutorizacionDialog;
